import is from '@sindresorhus/is';
import { type FunctionComponent, useState } from 'react';

import { Spinner } from '~/ui/core/spinner/Spinner';
import { ActionDock } from '~/ui/domain/chat/ActionDock';
import { BundleCard } from '~/ui/domain/chat/BundleCard';
import { StatefulButton } from '~/ui/domain/chat/StatefulButton';
import { UserBubble } from '~/ui/domain/chat/UserBubble';

import {
    HELLO_HITL_TOOL_ID,
    type HelloHitlResponseEvent,
    type QuestionnaireBlock,
    type QuestionnaireQuestion,
} from '../../data-contract/chat';
import { useSubmitChatInteractionCallback } from '../api';
import type { StructuredRenderer } from './types';

const BundleBlock: FunctionComponent<{ readonly question: QuestionnaireQuestion }> = ({ question }) => {
    const lead = <BundleCard.Section label="Show Hitl">{question.label}</BundleCard.Section>;

    if (!is.undefined(question.answer)) {
        return (
            <>
                <BundleCard lead={lead} />
                <UserBubble>{question.answer}</UserBubble>
            </>
        );
    }

    return <BundleCard lead={lead} />;
};

const HelloHitlInline: FunctionComponent<{ readonly event: HelloHitlResponseEvent }> = ({ event }) => (
    <div className="flex flex-col gap-s">
        {event.payload.blocks.map((block) =>
            block.questions.map((question) => (
                <BundleBlock key={`${block.blockId}-${question.actionId}`} question={question} />
            )),
        )}
    </div>
);

const HelloHitlInputOverride: FunctionComponent<{
    readonly event: HelloHitlResponseEvent;
    readonly block: QuestionnaireBlock;
    readonly question: QuestionnaireQuestion;
}> = ({ event, block, question }) => {
    const callback = useSubmitChatInteractionCallback();
    const [pendingLabel, setPendingLabel] = useState<string | null>(null);

    const submit = (value: string): void => {
        setPendingLabel(value);
        callback.mutate({
            blockId: block.blockId,
            eventId: event.eventId,
            payload: {
                actions: [{ actionId: question.actionId, input: value, type: 'input' }],
            },
        });
    };

    const sortedOptions = [...question.options].sort((a, b) =>
        a.recommended === b.recommended ? 0 : a.recommended ? -1 : 1,
    );

    return (
        <ActionDock>
            {sortedOptions.map((option) => (
                <StatefulButton
                    variant={option.recommended ? 'default' : 'subtle'}
                    key={option.label}
                    disabled={!is.null(pendingLabel)}
                    status={pendingLabel === option.label ? <Spinner /> : null}
                    onClick={() => submit(option.label)}
                >
                    {option.label}
                </StatefulButton>
            ))}
        </ActionDock>
    );
};

export const helloHitlRenderer: StructuredRenderer<HelloHitlResponseEvent> = {
    renderInline: (event) => <HelloHitlInline event={event} />,
    renderInputOverride: (event) => {
        const block = event.payload.blocks[0];
        const question = block?.questions[0];
        if (is.undefined(block) || is.undefined(question)) return null;
        if (!is.undefined(question.answer)) return null;
        return <HelloHitlInputOverride event={event} block={block} question={question} />;
    },
    toolId: HELLO_HITL_TOOL_ID,
};
